import React from "react";
import PropTypes from "prop-types";

import { extent, max, min } from "d3-array";
import { axisBottom, axisLeft } from "d3-axis";
import { select } from "d3-selection";
import { line } from "d3-shape";
import { utcParse } from "d3-time-format";

const parseTime = utcParse("%Y-%m-%dT%H:%M:%S.%LZ");

const Chart = props => {
  const {
    data,
    width,
    height,
    margin,
    x,
    y,
    getX,
    getY,
    rectData,
    depthBounds,
    rectStartAccessor,
    rectEndAccessor,
    bottomDepth,
    bottomDatetime
  } = props;

  const xScale = x.domain(extent(data, getX)).range([0, width]);
  // give y scale room above and below cast data
  const yScale = y
    .domain([min(data, getY) * 1.1, max(data, getY) + 1])
    .range([height, 0]);

  const xAxis = axisBottom().scale(xScale);
  const yAxis = axisLeft().scale(yScale);
  const path = line()
    .x(d => xScale(getX(d)))
    .y(d => yScale(getY(d)));

  return (
    <svg
      width={width + margin.top + margin.bottom}
      height={height + margin.left + margin.right}
    >
      <g
        className="container"
        transform={`translate(${margin.left},${margin.top})`}
      >
        <g
          className="x axis"
          ref={node => select(node).call(xAxis)}
          transform={`translate(0,${height})`}
        />
        <g className="y axis" ref={node => select(node).call(yAxis)} />
        <g className="background">
          <rect
            height={height}
            width={width - 1}
            x={1}
            y={0}
            style={{
              opacity: "1",
              fill: "white",
              stroke: "black",
              strokeWidth: 1.5
            }}
          />
        </g>
        <g className="line">
          <path
            d={path(data)}
            style={{
              fill: "transparent",
              stroke: "steelblue",
              strokeWidth: 2
            }}
          />
        </g>
        <g className="depth">
          <line
            x1={0}
            x2={width}
            y1={yScale(bottomDepth)}
            y2={yScale(bottomDepth)}
            style={{
              stroke: "#B60C0F",
              strokeWidth: 2
            }}
          />
        </g>
        <g className="time">
          <line
            x1={xScale(parseTime(bottomDatetime))}
            x2={xScale(parseTime(bottomDatetime))}
            y1={0}
            y2={height}
            style={{
              stroke: "grey",
              strokeDasharray: 10,
              strokeWidth: 2
            }}
          />
        </g>
        <g className="rects">
          <rect
            height={height}
            width={xScale(rectStartAccessor(rectData))}
            x={0}
            style={{ opacity: "0.3", fill: "grey" }}
          />
        </g>{" "}
        <g className="rects">
          <rect
            height={height}
            width={width - xScale(rectEndAccessor(rectData))}
            x={xScale(rectEndAccessor(rectData))}
            style={{ opacity: "0.3", fill: "grey" }}
          />
        </g>
        <g className="rects">
          <rect height={height} width={xScale(depthBounds[0])} x={0} />
        </g>
        <g className="rects">
          <rect
            height={height}
            width={width - xScale(depthBounds[1])}
            x={xScale(depthBounds[1])}
          />
        </g>
      </g>
      {props.children}
    </svg>
  );
};

Chart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  margin: PropTypes.shape({
    // eslint-disable-line react/forbid-prop-types
    top: PropTypes.number.isRequired,
    right: PropTypes.number.isRequired,
    bottom: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired
  }).isRequired,
  getX: PropTypes.func.isRequired,
  getY: PropTypes.func.isRequired,
  x: PropTypes.func.isRequired,
  y: PropTypes.func.isRequired,
  depthBounds: PropTypes.arrayOf(PropTypes.number).isRequired,
  rectData: PropTypes.shape({
    startDt: PropTypes.string.isRequired,
    endDt: PropTypes.string.isRequired,
    bottomDepth: PropTypes.number.isRequired
  }).isRequired,
  rectStartAccessor: PropTypes.func.isRequired,
  rectEndAccessor: PropTypes.func.isRequired,
  bottomDatetime: PropTypes.string.isRequired,
  bottomDepth: PropTypes.number.isRequired,
  children: PropTypes.node
};

Chart.defaultProps = {
  children: null
};

export default Chart;
